import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

const API = import.meta.env.VITE_API_URL ?? '';

/**
 * Delivery Tracking View — list deliveries, status transitions, share with drivers/buyers.
 * Also includes the Harvest Dashboard Widget.
 */
export default function DeliveryTracking({ token, onClose, onShare }) {
  const { t } = useTranslation();
  const [deliveries, setDeliveries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [delRes, sumRes] = await Promise.allSettled([
        fetch(`${API}/api/v1/aif/deliveries`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/v1/aif/deliveries/summary`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (delRes.status === 'fulfilled' && delRes.value.ok) {
        const data = await delRes.value.json();
        setDeliveries(data.deliveries || []);
      }
      if (sumRes.status === 'fulfilled' && sumRes.value.ok) {
        setSummary(await sumRes.value.json());
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const updateStatus = async (id, newStatus) => {
    try {
      await apiClient.patch(`/api/v1/aif/deliveries/${id}`, { status: newStatus }, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
      fetchData();
    } catch { /* ApiError */ }
  };

  const STATUS_COLORS = {
    PLANNED: 'bg-gray-100 text-gray-600',
    LOADING: 'bg-amber-100 text-amber-700',
    IN_TRANSIT: 'bg-blue-100 text-blue-700',
    DELIVERED: 'bg-purple-100 text-purple-700',
    CONFIRMED: 'bg-green-100 text-green-700',
  };

  const STATUS_ICONS = {
    PLANNED: '\uD83D\uDCCB',
    LOADING: '\uD83D\uDCE6',
    IN_TRANSIT: '\uD83D\uDE9A',
    DELIVERED: '\uD83C\uDFE0',
    CONFIRMED: '\u2705',
  };

  const NEXT_STATUS = {
    PLANNED: 'LOADING',
    LOADING: 'IN_TRANSIT',
    IN_TRANSIT: 'DELIVERED',
  };

  const NEXT_LABELS = {
    PLANNED: t('delivery.startLoading'),
    LOADING: t('delivery.departed'),
    IN_TRANSIT: t('delivery.arrived'),
  };

  return (
    <div className="space-y-4">
      {/* Harvest Summary Widget */}
      {summary && (
        <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-4">
          <h4 className="text-sm font-semibold text-[var(--aif-text-primary)] mb-2">{t('delivery.harvestStatus')}</h4>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-lg font-bold text-[var(--aif-color-primary)]">{summary.active_count}</div>
              <div className="text-[10px] text-[var(--aif-text-muted)]">{t('delivery.active')}</div>
            </div>
            <div>
              <div className="text-lg font-bold text-blue-600">{summary.in_transit_count}</div>
              <div className="text-[10px] text-[var(--aif-text-muted)]">{t('delivery.inTransit')}</div>
            </div>
            <div>
              <div className="text-lg font-bold text-green-600">{(summary.total_confirmed_kg / 1000).toFixed(1)}t</div>
              <div className="text-[10px] text-[var(--aif-text-muted)]">{t('delivery.delivered')}</div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[var(--aif-text-primary)]">{t('delivery.title')}</h3>
        <button
          onClick={() => setShowForm(true)}
          className="px-3 py-1.5 rounded-lg bg-[var(--aif-color-primary)] text-white text-xs font-medium"
        >
          + {t('delivery.new')}
        </button>
      </div>

      {/* New Delivery Form */}
      {showForm && (
        <DeliveryForm
          token={token}
          onCreated={() => { setShowForm(false); fetchData(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Delivery List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
        </div>
      ) : deliveries.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-3xl mb-2">{'\uD83D\uDE9A'}</div>
          <p className="text-sm text-[var(--aif-text-muted)]">{t('delivery.noDeliveries')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {deliveries.map(d => (
            <div key={d.id} className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-lg">{STATUS_ICONS[d.status] || '\uD83D\uDE9A'}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[var(--aif-text-primary)] truncate">{d.product_name}</div>
                    <div className="text-xs text-[var(--aif-text-muted)]">{d.quantity} {d.unit}</div>
                  </div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[d.status] || ''}`}>
                  {d.status}
                </span>
              </div>

              {/* Origin → Destination */}
              <div className="text-xs text-[var(--aif-text-secondary)] space-y-0.5">
                {d.origin_name && <div>{d.origin_name}</div>}
                {d.destination_name && <div>&rarr; {d.destination_name}</div>}
                {d.driver_name && <div>{t('delivery.driver')}: {d.driver_name}</div>}
              </div>

              {/* Status transition button */}
              <div className="flex gap-2">
                {NEXT_STATUS[d.status] && (
                  <button
                    onClick={() => updateStatus(d.id, NEXT_STATUS[d.status])}
                    className="px-3 py-1.5 rounded-lg bg-[var(--aif-color-primary)] text-white text-xs font-medium"
                  >
                    {NEXT_LABELS[d.status]}
                  </button>
                )}
                {d.status !== 'CONFIRMED' && onShare && (
                  <button
                    onClick={() => onShare({ type: 'delivery', id: d.id, label: `${d.product_name} (${d.quantity} ${d.unit})` })}
                    className="px-3 py-1.5 rounded-lg bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-xs text-[var(--aif-text-secondary)]"
                  >
                    {t('delivery.share')}
                  </button>
                )}
              </div>

              {/* Confirmed info */}
              {d.status === 'CONFIRMED' && (
                <div className="text-xs text-green-600 space-y-0.5">
                  {d.confirmed_quantity && <div>{t('delivery.confirmedQty')}: {d.confirmed_quantity} {d.unit}</div>}
                  {d.confirmed_moisture_pct && <div>{t('delivery.moisture')}: {d.confirmed_moisture_pct}%</div>}
                  {d.weighbridge_ticket && <div>{t('delivery.weighbridge')}: {d.weighbridge_ticket}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Delivery Form ───────────────────────────────────────────────────────────

function DeliveryForm({ token, onCreated, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    product_name: '', quantity: '', unit: 'kg', moisture_pct: '',
    origin_name: '', destination_name: '', driver_name: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async () => {
    if (!form.product_name || !form.quantity) return;
    setSaving(true);
    try {
      const body = {
        ...form,
        quantity: parseFloat(form.quantity),
        moisture_pct: form.moisture_pct ? parseFloat(form.moisture_pct) : undefined,
      };
      await apiClient.post('/api/v1/aif/deliveries', body, { queueOnOffline: true });
      onCreated();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const inputClass = "w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] placeholder-[var(--aif-text-muted)] focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]/30";

  return (
    <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-4 space-y-3">
      <h4 className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('delivery.new')}</h4>
      <input value={form.product_name} onChange={e => update('product_name', e.target.value)} placeholder={t('delivery.product')} className={inputClass} />
      <div className="flex gap-2">
        <input type="number" value={form.quantity} onChange={e => update('quantity', e.target.value)} placeholder={t('procurement.quantity')} className={`flex-1 ${inputClass}`} />
        <select value={form.unit} onChange={e => update('unit', e.target.value)} className={`w-20 ${inputClass}`}>
          <option value="kg">kg</option>
          <option value="t">t</option>
        </select>
        <input type="number" step="0.1" value={form.moisture_pct} onChange={e => update('moisture_pct', e.target.value)} placeholder="% H2O" className={`w-24 ${inputClass}`} />
      </div>
      <input value={form.origin_name} onChange={e => update('origin_name', e.target.value)} placeholder={t('delivery.origin')} className={inputClass} />
      <input value={form.destination_name} onChange={e => update('destination_name', e.target.value)} placeholder={t('delivery.destination')} className={inputClass} />
      <input value={form.driver_name} onChange={e => update('driver_name', e.target.value)} placeholder={t('delivery.driverName')} className={inputClass} />
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 py-2 rounded-lg text-sm text-[var(--aif-text-muted)] bg-[var(--aif-bg-elevated)]">{t('procurement.cancel')}</button>
        <button onClick={handleSubmit} disabled={saving || !form.product_name || !form.quantity} className="flex-1 py-2 rounded-lg text-sm text-white bg-[var(--aif-color-primary)] font-medium disabled:opacity-50">{saving ? '...' : t('procurement.create')}</button>
      </div>
    </div>
  );
}

/**
 * Harvest Dashboard Widget — compact summary for DashboardPage.
 */
export function HarvestWidget({ token }) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/v1/aif/deliveries/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setSummary(await res.json());
      } catch { /* ignore */ }
    })();
  }, []);

  if (!summary || (summary.active_count === 0 && summary.total_confirmed_kg === 0)) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[var(--aif-text-primary)]">{t('delivery.harvestStatus')}</span>
        <span className="text-xs text-[var(--aif-text-muted)]">&rsaquo;</span>
      </div>
      {summary.active_deliveries && summary.active_deliveries.length > 0 && (
        <div className="text-xs text-[var(--aif-text-secondary)]">
          {summary.active_deliveries[0].product_name} &middot; {summary.in_transit_count} {t('delivery.trucksEnRoute')} &middot; {(summary.total_confirmed_kg / 1000).toFixed(1)}t {t('delivery.delivered')}
        </div>
      )}
    </div>
  );
}
